import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { LegalEditorCard } from '@/features/legal/LegalEditorCard'
import { LegalLanguagePicker } from '@/features/legal/LanguagePicker'
import { VersionHistoryCard } from '@/features/legal/VersionHistoryCard'
import {
  LEGAL_DOCUMENT_TYPES,
  legalDocumentsQuery,
  type LegalAuthorableLocale,
  type LegalDocumentType,
} from '@/features/legal/legal.queries'
import { Badge, ErrorState, LoadingState, PageHeader, Tabs, TabPanel, type TabItem } from '@/ui'

/**
 * The privacy policy and the terms of service.
 *
 * One tab per document, because they are two separate records with separate version numbers and
 * separate consent — not two halves of one page. Everything here is gated on `legal.manage`,
 * enforced by the server on every request; the navigation entry hiding is a courtesy
 * (best practices §1).
 */
export const Route = createFileRoute('/_authed/legal')({
  component: LegalPage,
})

function LegalPage() {
  const { t } = useTranslation()
  const [active, setActive] = useState<LegalDocumentType>('TERMS_OF_SERVICE')
  const [locale, setLocale] = useState<LegalAuthorableLocale>('en')

  const query = useQuery(legalDocumentsQuery)

  if (query.isPending) return <LoadingState />
  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
      />
    )
  }

  const documents = query.data
  const current = documents.find((document) => document.type === active)

  const tabs: TabItem[] = LEGAL_DOCUMENT_TYPES.map((type) => ({
    id: type,
    label: type === 'PRIVACY_POLICY' ? t('legal.privacyPolicy') : t('legal.termsOfService'),
  }))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('legal.title')} description={t('legal.description')} />

      <Tabs
        tabs={tabs}
        activeId={active}
        onChange={(id) => {
          setActive(id as LegalDocumentType)
        }}
      />

      {current ? (
        <TabPanel id={active}>
          <div className="flex flex-col gap-6">
            <LiveSummary document={current} />
            <LegalLanguagePicker document={current} active={locale} onChange={setLocale} />
            {/*
              Keyed on the document type so switching tabs remounts the editor. Without the key
              React reuses the component and its unsaved-edit state, which would carry the terms
              somebody was writing into the privacy policy's textarea.
            */}
            {/*
              Keyed on the language too: switching from English to French with an unsaved edit in
              the box would otherwise carry the English draft into the French one and save it
              there.
            */}
            <LegalEditorCard key={`${active}-${locale}`} document={current} locale={locale} />
            <VersionHistoryCard
              key={`${active}-history`}
              type={active}
              consentFloor={current.consentFloorVersionNumber}
            />
          </div>
        </TabPanel>
      ) : (
        // The server knows a document type this build does not draw a tab for. Saying so beats
        // an empty page.
        <ErrorState error={new Error(t('legal.unknownDocument'))} />
      )}
    </div>
  )
}

/** What is live right now, in one line, above the editor that is about to replace it. */
function LiveSummary({
  document,
}: {
  document: {
    current: { versionNumber: number; effectiveAt: string } | null
    scheduled: { versionNumber: number; effectiveAt: string } | null
  }
}) {
  // The panel's language, not the machine's: `undefined` here put French dates on an English page.
  const { t, i18n } = useTranslation()

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-surface px-4 py-3 text-sm">
      {document.current ? (
        <>
          <Badge tone="success">{t('legal.live')}</Badge>
          <span className="text-text">
            {t('legal.versionNumber', { version: document.current.versionNumber })}
          </span>
          <span className="text-text-muted">
            {new Date(document.current.effectiveAt).toLocaleDateString(i18n.language, {
              dateStyle: 'long',
            })}
          </span>
        </>
      ) : (
        <>
          <Badge tone="warning">{t('legal.nothingPublished')}</Badge>
          <span className="text-text-muted">{t('legal.nothingPublishedHint')}</span>
        </>
      )}

      {document.scheduled ? (
        <span className="ml-auto flex items-center gap-2">
          <Badge tone="info">{t('legal.scheduled')}</Badge>
          <span className="text-text-muted">
            {t('legal.versionNumber', { version: document.scheduled.versionNumber })} ·{' '}
            {new Date(document.scheduled.effectiveAt).toLocaleString(i18n.language, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </span>
        </span>
      ) : null}
    </div>
  )
}
